import useApiRequest from "@/lib/hooks/use-request";
import { UserRole } from "@/lib/interfaces/user";
import { UserListResponseDto } from "@/lib/interfaces/users";
import { DataTableProps } from "@/lib/interfaces/utils";
import { Button } from "@/ui-components/ui/button";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import { EyeClosedIcon, EyeOpenIcon } from "@radix-ui/react-icons";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { Clock, User } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const WebhooksDataTable = ({
  data,
  isLoading,
  pageCount,
  pagination,
  setPagination,
  setReload,
}: DataTableProps<UserListResponseDto>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowSelection, setRowSelection] = useState({});
  const navigate = useNavigate();
  const { apiRequest } = useApiRequest();

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case "ADMIN":
        return "bg-red-50 text-red-700 ring-red-700/10";
      case "SUPERADMIN":
        return "bg-yellow-50 text-yellow-700 ring-yellow-700/10";
      default:
        return "bg-blue-50 text-blue-700 ring-blue-700/10";
    }
  };

  const columns: ColumnDef<UserListResponseDto>[] = [
    {
      accessorKey: "name",
      header: () => {
        return (
          <div className="flex items-center">
            <p>Name</p>
          </div>
        );
      },
      cell: ({ row }) => {
        const fullName = `${row.original.firstName} ${row.original.lastName}`;
        return (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <div className="flex flex-col">
              <p className="font-medium">{fullName}</p>
              {row.original.ownUser && <span className="text-xs text-gray-500">(Sie)</span>}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "role",
      header: () => {
        return (
          <div className="flex items-center">
            <p>Rolle</p>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex">
            <span
              className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getRoleColor(
                row.original.role
              )}`}
            >
              {row.original.role}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "salesRankTitle",
      header: () => {
        return (
          <div className="flex items-center">
            <p>Verkaufsrang</p>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex">
            <span className="text-sm">{row.original.salesRankTitle || "-"}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "lastLogin",
      header: () => {
        return (
          <div className="flex items-center">
            <p>Letzter Login</p>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>{format(new Date(row.original.lastLogin), "dd.MM.yyyy HH:mm")}</span>
          </div>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const archiveToggle = async () => {
          await apiRequest(
            `settings/users/${row.original._id}/${row.original.isArchive ? "unarchive" : "archive"}`,
            "PATCH",
            {
              toast: {
                toastText: `Benutzer ${row.original.isArchive ? "wiederhergestellt" : "archiviert"}`,
              },
            }
          );
          setReload((prev) => !prev);
        };
        return (
          <div className="flex justify-end">
            {row.original.isArchiveable && (
              <div className="flex items-center gap-1 max-w-12 mr-2">
                <ConfirmationDialog
                  onConfirm={archiveToggle}
                  title={row.original.isArchive ? "Benutzer wiederherstellen" : "Benutzer archivieren"}
                  description={
                    row.original.isArchive
                      ? "Möchten Sie den Benutzer wirklich wiederherstellen?"
                      : "Möchten Sie den Benutzer wirklich archivieren?"
                  }
                  variant={row.original.isArchive ? "default" : "destructive"}
                >
                  <Button variant="link">
                    {row.original.isArchive ? (
                      <EyeClosedIcon className="h-5 w-5" />
                    ) : (
                      <EyeOpenIcon className="h-5 w-5" />
                    )}
                  </Button>
                </ConfirmationDialog>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    initialState: { pagination: { pageSize: 20 } },
    state: {
      pagination,
      rowSelection,
    },
  });

  if (isLoading) {
    return <div>Lädt...</div>;
  }

  return (
    <div className="w-full">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={`cursor-pointer hover:bg-gray-100 ${row.original.isArchive ? "bg-gray-200" : ""}`}
                  onClick={() => navigate(`/settings/users/${row.original._id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Keine Benutzer gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.previousPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", table.getState().pagination.pageIndex.toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanPreviousPage()}
        >
          Letzte
        </Button>
        <span className="text-sm text-[#D0D5DD]">
          Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount()}
        </span>
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.nextPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", (table.getState().pagination.pageIndex + 2).toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanNextPage()}
        >
          Nächste
        </Button>
      </div>
    </div>
  );
};

export default WebhooksDataTable;
